import {css} from 'aphrodite';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {
  BenchlingExpression,
  BenchlingExpressionFunction,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {BenchlingFunctionSignature} from 'benchling-expressions/BenchlingFunctionSignatures';
import BenchlingConfig from 'BenchlingConfig';
import {AnalyticsMetadataForContext} from 'constants/AnalyticsConstants';
import {BenchlingTypeDef} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {ProcessGraphBuilderNode} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import MultipleConditionConfig, {
  DEFAULT_OPERATOR,
} from 'processes/modals/router-config/MultipleConditionConfig';
import {
  CreateOrUpdateRouterFunctionSpec,
  CreateRouterFunctionSpec,
  sharedStyles,
} from 'processes/modals/router-config/RouterConfigConstants';
import {getPartialExpression} from 'processes/modals/router-config/RouterConfigUtils';
import {expressionFunctionIsBoolean} from 'processes/modals/router-config/RouterConfigUtils';
import {Schema} from 'schemas/SchemaConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {DeepPartial} from 'util/Types';

import {ProcessOutputSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessOutputSchemaGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import LogicalFunctionConfig from './LogicalFunctionConfig';

interface ConditionalLogicConfigErrors {
  isNextNodeDuplicate: boolean;
}

export interface RouterRuleComponentProps {
  routerNodeClientId: string;
  flowchartClientId: string;
  onIsRouterFunctionNameBeingEdited: (isBeingEdited: boolean) => void;
  functionSpec: CreateOrUpdateRouterFunctionSpec & CreateRouterFunctionSpec;
  setFunctionSpec: (newFunctionSpec: CreateOrUpdateRouterFunctionSpec & CreateRouterFunctionSpec) => void;
  inputNode: ProcessGraphBuilderNode;
  flowchartTaskSchema: Schema;
  immediateChildNodes: Array<ProcessGraphBuilderNode>;
  inputSchema: ProcessTaskSchemaGraphQL | ProcessOutputSchemaGraphQL;
  didInitialFlowchartHaveChainedRouters: boolean;
  doesCurrentFlowchartHaveChainedRouters: boolean;
  analyticsMetadata: AnalyticsMetadataForContext;
  dataTestAttrs?: DataTestAttrs;
  errors: ConditionalLogicConfigErrors;
  isMismatchedTypesError: boolean;
  operatorDisplayName: string | null;
  selectorId: string | null;
  lookupConfig: LookupConfigSpec | null;
  allowedOperators: Array<BenchlingFunctionSignature> | null;
  isMultiplex: boolean;
  typeDef: BenchlingTypeDef | null;
}

/* Container component for router rule inputs */
export default function RouterRuleComponent(props: RouterRuleComponentProps): JSX.Element | null {
  const {expression} = useMemo(() => {
    const expression = getPartialExpression(
      props.functionSpec.expression as DeepPartial<BenchlingExpression>
    );

    return {
      expression,
    };
  }, [props.functionSpec.expression]);

  if (props.functionSpec.isDefault) {
    return null;
  }

  function wrap(
    expr: DeepPartial<BenchlingExpressionFunction> | null
  ): DeepPartial<BenchlingExpressionFunction> {
    return {
      type: BenchlingExpressionType.FUNCTION,
      name: DEFAULT_OPERATOR.functionName,
      parameters: [
        {
          name: DEFAULT_OPERATOR.parameters[0].spreadParameterName,
          expression: expr ?? undefined,
        },
      ],
    };
  }

  if (BenchlingConfig.WORKFLOWS_ENABLE_AND_OR_CONDITIONS) {
    // MultipleConditionConfig expects a boolean expression
    return (
      <MultipleConditionConfig
        routerNodeClientId={props.routerNodeClientId}
        flowchartClientId={props.flowchartClientId}
        expression={
          expression != null && expressionFunctionIsBoolean(expression) ? expression : wrap(expression)
        }
        setExpression={setExpressionInFunctionSpec}
        onIsRouterFunctionNameBeingEdited={props.onIsRouterFunctionNameBeingEdited}
        inputNode={props.inputNode}
        inputSchema={props.inputSchema}
        immediateChildNodes={props.immediateChildNodes}
        flowchartTaskSchema={props.flowchartTaskSchema}
        didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
        doesCurrentFlowchartHaveChainedRouters={props.doesCurrentFlowchartHaveChainedRouters}
        analyticsMetadata={props.analyticsMetadata}
        dataTestAttrs={{component: 'RouterRuleComponent', element: 'MultipleConditionConfig', key: '0.0'}}
        errors={props.errors}
        recursiveDepth={0}
      />
    );
  }

  // Singular non-boolean conditions should be unwrapped so as to avoid changing
  // expressions that were created with the config flag disabled, and so that they can be opened
  // if the config flag is turned off again
  function setExpressionInFunctionSpec(expr: DeepPartial<BenchlingExpression>): void {
    let funcExpr = getPartialExpression(expr);
    if (expressionFunctionIsBoolean(funcExpr)) {
      assert(funcExpr != null, 'Expected expression to be defined');
      if (funcExpr.parameters != null && funcExpr.parameters.length === 1) {
        const firstExpression = getPartialExpression(funcExpr.parameters[0]!.expression);
        if (!expressionFunctionIsBoolean(firstExpression)) {
          funcExpr = firstExpression;
        }
      }
    }
    props.setFunctionSpec({...props.functionSpec, expression: funcExpr, isConfigured: false});
  }

  return expression != null ? (
    <div className={css(sharedStyles.row)}>
      If
      <LogicalFunctionConfig
        routerNodeClientId={props.routerNodeClientId}
        flowchartClientId={props.flowchartClientId}
        expression={expression}
        setExpression={setExpressionInFunctionSpec}
        inputNode={props.inputNode}
        flowchartTaskSchema={props.flowchartTaskSchema}
        inputSchema={props.inputSchema}
        analyticsMetadata={props.analyticsMetadata}
        errors={props.errors}
        dataTestAttrs={{component: 'RouterRuleComponent', element: 'LogicalFunctionConfig', key: '0'}}
      />
    </div>
  ) : null;
}
